import { T } from '@tolgee/react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import theme from '../../../../assets/theme';
import { useDynamicLessonStateContext } from '../../../../hooks/useCreateLessonState';
import { useMetrics } from '../../../../hooks/useMetrics';
import { MaterialAttachment } from '../../../../hooks/useUploadMaterialAttachment';
import { LLMMessageRole } from '../../../../services/backendService/backendService';
import { MarkdownSlateViewer } from '../../../_organisms/DesignerTabPane/AgentTab/MarkdownSlateViewer/MarkdownSlateViewer';
import { AgentStateBadge } from './AgentStateBadge';
import { ChatAttachments } from './ChatAttachments';
import { CuripodChatAvatar } from './CuripodChatAvatar';
import { useAgentHistoryMessages } from './use-agent-history-messages';

export function ChatHistoryList({
  onChatSubmitted,
  attachments,
}: {
  onChatSubmitted?: number;
  attachments: Array<MaterialAttachment>;
}) {
  const metrics = useMetrics();

  const promptVariables = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.promptVariables,
  );
  const isObjectiveReady = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.isObjectiveReady,
  );
  const error = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.error,
  );
  const agentState = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.state,
  );

  const streamingMessageRef = useRef<HTMLDivElement>(null);

  const messages = useAgentHistoryMessages();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  // Check if user has scrolled away from bottom
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollOffsetFromBottom =
        container.scrollHeight - (container.scrollTop + container.clientHeight);
      const isNearBottom = scrollOffsetFromBottom <= 90;
      setShouldAutoScroll(isNearBottom);
    }
  };

  // Re-enable auto-scroll when a chat is submitted
  useEffect(() => {
    if (onChatSubmitted !== undefined) {
      setShouldAutoScroll(true);
    }
  }, [onChatSubmitted]);

  // Auto-scroll only if user hasn't manually scrolled up
  useEffect(() => {
    if (shouldAutoScroll && scrollContainerRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          // Add small delay to prevent conflicts with keyboard/viewport changes
          setTimeout(() => {
            if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollTo({
                top: scrollContainerRef.current.scrollHeight,
                behavior: 'smooth',
              });
            }
          }, 100);
        }
      });
    }
  }, [messages, shouldAutoScroll]);

  // Track when objective becomes ready
  useEffect(() => {
    if (isObjectiveReady && promptVariables) {
      metrics.logEvent('CreatePage.ObjectiveRefinement.ObjectiveReady', {
        chatHistoryLength: messages.length,
        // sessionLength: 0,
        promptVariables: {
          learningObjective: promptVariables.learningObjective,
          grade: promptVariables.grade,
          subject: promptVariables.subject,
          languages: promptVariables.languages,
          lessonCategory: promptVariables.lessonCategory,
          standardAlignment: promptVariables.standardAlignment,
        },
        // Track all metadata about the ready objective
        objectiveMetadata: {
          learningObjective: promptVariables.learningObjective,
          grade: promptVariables.grade,
          subject: promptVariables.subject,
          languages: promptVariables.languages,
          lessonCategory: promptVariables.lessonCategory,
          standardAlignment: promptVariables.standardAlignment,
          chatHistoryLength: messages.length,
          // sessionLength: 0,
        },
      });
    }
  }, [isObjectiveReady, promptVariables, messages.length, metrics]);

  return (
    <ChatContainer
      $hasHistory={messages.length > 0}
      ref={scrollContainerRef}
      onScroll={handleScroll}
    >
      {messages.length > 0 && (
        <ChatHistory>
          {messages.map((message, index) => {
            return (
              <>
                {message.attachments && message.attachments.length > 0 && (
                  // use attachments from ObjectiveRefinement since they enable us to display the original attachment, not just the markdown file
                  <ChatAttachments attachments={attachments ?? message.attachments} />
                )}
                {message.content && (
                  <ChatMessageWrapper
                    // this is a hacky solution to UI blinking--next step is to manage state via react query cache
                    // and only let a ChatMessage component read from the react query cache
                    key={
                      index === messages.length - 1 && message.role === 'ASSISTANT'
                        ? 'streaming message'
                        : message.id
                    }
                    role={message.role}
                    style={{ ...(message.role === 'ASSISTANT' && { width: '95%' }) }}
                    ref={
                      message.isStreamingMessage ||
                      message.id === 'streaming' ||
                      (index === messages.length - 1 && message.role === 'ASSISTANT')
                        ? streamingMessageRef
                        : null
                    }
                  >
                    {message.role === 'ASSISTANT' && !message.content && (
                      <AgentStateBadge agentState={agentState} />
                    )}
                    {message.role === 'ASSISTANT' && !!message.content && (
                      <CuripodChatAvatar />
                    )}

                    <MarkdownSlateViewer content={message.content ?? ''} />
                  </ChatMessageWrapper>
                )}
              </>
            );
          })}

          {error && (
            <ChatMessageWrapper role="ASSISTANT">
              <p>
                {error.error.message || (
                  <T
                    keyName="An error ocurred. Please try again"
                    defaultValue="An error ocurred. Please try again"
                  />
                )}
              </p>
            </ChatMessageWrapper>
          )}
          <div
            aria-hidden="true"
            style={{
              height: '12px',
              flexShrink: 0,
            }}
          />
        </ChatHistory>
      )}
    </ChatContainer>
  );
}

const ChatContainer = styled.div<{ $hasHistory: boolean }>`
  width: 100%;
  flex: ${({ $hasHistory }) => ($hasHistory ? '1' : '0')};
  margin-bottom: ${({ $hasHistory }) => ($hasHistory ? '0px' : '0px')};
  padding: 20px 20px 12px 20px;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  opacity: ${({ $hasHistory }) => ($hasHistory ? '1' : '0')};
  transition: flex 0.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease-out 0.2s;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
`;

const ChatHistory = styled.div`
  flex: 1;
  display: flex;
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  flex-direction: column;
  gap: 12px;
  padding: 8px;
  min-height: 0;
`;

const ChatMessageWrapper = styled.div<{ role: LLMMessageRole }>`
  padding: 12px;
  border-radius: 8px;
  max-width: 95%;
  align-self: ${props => (props.role === 'USER' ? 'flex-end' : 'flex-start')};
  background-color: ${props =>
    props.role === 'USER' ? theme.colors.paleBeige : theme.colors.lessTranslucentWhite};
  color: ${props => (props.role === 'USER' ? theme.colors.black : theme.colors.black)};
  font-size: 16px;
  font-weight: 600;
`;
